/**
 * Versioned HTTP contract — `finance-v1` — for the finance api (FINAPP-1, ADR 0076).
 *
 * The wire shapes `finance-api` RETURNS and the finance web console (+ any MCP client, indirectly)
 * PARSES — so both sides "parse, don't validate" against the same schemas without importing one
 * another (ADR 0005). Reuses the shared `{data,error,meta}` envelope (`./v1.js`). Domain types live
 * in `../finance.js`; these are the transport shapes (kept in lockstep). The api parses the raw
 * Google-Sheet rows into these shapes at the edge, so a drifted sheet fails loudly at the boundary.
 */
import { z } from 'zod';
import { apiEnvelope } from './v1.js';
import { FINANCE_SOURCE_KINDS, KPI_UNITS, KPI_DIRECTIONS, PL_CATEGORIES } from '../finance.js';

/** Contract namespace (also the URL prefix `/api/finance/v1`). Envelope `meta.version` stays the shared `v1`. */
export const FINANCE_HTTP_CONTRACT_VERSION = 'finance-v1' as const;

const key = z.string().min(1).max(64);
const label = z.string().min(1).max(160);
const owner = z.string().min(1).max(120);
const epochMs = z.number().int().nonnegative();
const amount = z.number().finite();

// ── KPI ────────────────────────────────────────────────────────────────────
export const FinanceKpiSchema = z.object({
  key,
  label,
  value: amount,
  unit: z.enum(KPI_UNITS),
  deltaPct: z.number().finite().optional(),
  direction: z.enum(KPI_DIRECTIONS).optional(),
  note: z.string().max(280).optional(),
});
export type FinanceKpiV1 = z.infer<typeof FinanceKpiSchema>;

// ── P&L line ─────────────────────────────────────────────────────────────────
export const FinancePlLineSchema = z.object({
  id: key,
  label,
  category: z.enum(PL_CATEGORIES),
  amount,
  note: z.string().max(280).optional(),
});
export type FinancePlLineV1 = z.infer<typeof FinancePlLineSchema>;

// ── Trend series ─────────────────────────────────────────────────────────────
export const FinanceTrendPointSchema = z.object({
  period: z.string().min(1).max(40),
  value: amount,
});
export type FinanceTrendPointV1 = z.infer<typeof FinanceTrendPointSchema>;

export const FinanceSeriesSchema = z.object({
  key,
  label,
  points: z.array(FinanceTrendPointSchema).max(240),
});
export type FinanceSeriesV1 = z.infer<typeof FinanceSeriesSchema>;

// ── Snapshot (the payload the console renders + the MCP tools read) ──────────
export const FinanceSnapshotSchema = z.object({
  ownerId: owner,
  title: z.string().min(1).max(120),
  /** ISO 4217, e.g. 'USD'. Kept 3 upper-case letters at the wire. */
  currency: z.string().regex(/^[A-Z]{3}$/),
  source: z.enum(FINANCE_SOURCE_KINDS),
  asOf: epochMs,
  generatedAt: epochMs,
  kpis: z.array(FinanceKpiSchema).max(24),
  pl: z.array(FinancePlLineSchema).max(200),
  series: z.array(FinanceSeriesSchema).max(12),
  notes: z.string().max(600).optional(),
});
export type FinanceSnapshotV1 = z.infer<typeof FinanceSnapshotSchema>;

export const FinanceSnapshotResponseSchema = apiEnvelope(FinanceSnapshotSchema);
export type FinanceSnapshotResponse = z.infer<typeof FinanceSnapshotResponseSchema>;

// ── GET /api/finance/health ──────────────────────────────────────────────────

/** Data source the api resolved at startup: `google-sheet` when creds are present, else `mock`. */
export const FinanceSourceModeSchema = z.enum(FINANCE_SOURCE_KINDS);
/** MCP server posture: `enabled` when `MCP_TOKEN` is set (bearer-gated), else `disabled`. */
export const McpModeSchema = z.enum(['enabled', 'disabled']);

/**
 * Planning-store posture (FINAPP-5, ADR 0084): `supabase` when the `SUPABASE_*` creds are present
 * (budgets persist), else `unavailable` — the budgeting layer reports itself down rather than
 * faking data (no in-memory fallback, ADR 0084 §3). Distinct from `source` (the sheet-backed
 * actuals), which stays mock-always. Optional so a client predating this field still parses.
 */
export const FinanceConfigStoreModeSchema = z.enum(['supabase', 'unavailable']);
export type FinanceConfigStoreMode = z.infer<typeof FinanceConfigStoreModeSchema>;

export const FinanceHealthDataSchema = z.object({
  status: z.literal('ok'),
  source: FinanceSourceModeSchema,
  mcp: McpModeSchema,
  /** Owner-scoped planning store posture (budgets/forecast). Absent on an api predating FINAPP-5. */
  configStore: FinanceConfigStoreModeSchema.optional(),
  /** Build identity of the deployed api (git SHA), or 'unknown' when not deployed via the pipeline. */
  version: z.string(),
});
export type FinanceHealthData = z.infer<typeof FinanceHealthDataSchema>;

export const FinanceHealthResponseSchema = apiEnvelope(FinanceHealthDataSchema);
export type FinanceHealthResponse = z.infer<typeof FinanceHealthResponseSchema>;
